// The Intercom messenger is loaded by the snippet in the Retailers Dashboard's
// index.html, which boots it anonymously. These helpers are the only place that
// talks to the messenger afterwards.
//
// Identity Verification (the `user_hash` HMAC) is deliberately NOT sent — it is
// off in the Intercom workspace. If someone turns it on, Intercom rejects an
// identified `update` and every conversation silently reverts to an anonymous
// visitor, which is the problem this code exists to solve. Turning it on needs a
// backend endpoint to sign the user id first.

export const INTERCOM_APP_ID = 'jvt2jset';

// Whether we've told Intercom who the user is in this page session. Guards
// `resetIntercom` so an anonymous visitor isn't churned through a needless
// shutdown/boot on every signed-out page.
let identified = false;

const callIntercom = (...args) => {
	if (typeof window === 'undefined' || typeof window.Intercom !== 'function') return false;
	window.Intercom(...args);
	return true;
};

const toUnixSeconds = (date) => {
	const ms = new Date(date).getTime();
	return Number.isNaN(ms) ? undefined : Math.floor(ms / 1000);
};

/**
 * Tell Intercom which retailer is signed in, so a conversation arrives attached
 * to a contact rather than as an anonymous visitor.
 *
 * @param {object} user - the Clerk user object from `useUser()`
 * @param {string} product - which dashboard they're chatting from, sent as a
 *   custom attribute on the contact. Only the Retailers Dashboard (`nearst`)
 *   loads the messenger today.
 */
export const identifyIntercomUser = (user, product) => {
	if (!user?.id) return false;

	const sent = callIntercom('update', {
		user_id: user.id,
		name: user.fullName || undefined,
		email: user.primaryEmailAddress?.emailAddress,
		created_at: user.createdAt ? toUnixSeconds(user.createdAt) : undefined,
		product
	});

	if (sent) identified = true;
	return sent;
};

/**
 * Drop the identified session on sign-out. Without the shutdown Intercom keeps
 * the previous retailer's conversation history in local storage, so the next
 * person to sign in on the same browser sees it.
 */
export const resetIntercom = () => {
	if (!identified) return false;
	if (!callIntercom('shutdown')) return false;

	callIntercom('boot', { app_id: INTERCOM_APP_ID });
	identified = false;
	return true;
};
